import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button/Button';
import { PopoverContent } from '@/shared/ui/Popover/components/PopoverContent';
import { PopoverRoot } from '@/shared/ui/Popover/components/PopoverRoot';
import { PopoverTrigger } from '@/shared/ui/Popover/components/PopoverTrigger';
import type { FormFieldSize } from '../../helpers/field-size';
import { formFieldSizeVariants } from '../../helpers/field-size';
import { selectTriggerStyles } from '../helpers/trigger-styles';
import type { SelectOption } from '../helpers/types';
import { normalizeOptionValue } from '../helpers/utils';
import { SearchableSingleSelectContent } from './SearchableSingleSelectContent';

type SearchableSingleSelectProps = {
  filteredOptions: SelectOption[];
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  selectValue: string;
  onValueChange: (value: string) => void;
  onClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size: FormFieldSize;
  disabled: boolean;
  rootClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  id?: string;
  showAllClear: boolean;
  placeholder: string;
  fixedBottomOptions?: SelectOption[];
  hasError: boolean;
  activeIndex: number;
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  options: SelectOption[];
};

export const SearchableSingleSelect = ({
  filteredOptions,
  searchValue,
  onSearchValueChange,
  selectValue,
  onValueChange,
  onClear,
  size,
  disabled,
  rootClassName,
  triggerClassName,
  contentClassName,
  id,
  showAllClear,
  placeholder,
  fixedBottomOptions,
  hasError,
  activeIndex,
  onSearchKeyDown,
  searchOpen,
  onSearchOpenChange,
  options,
}: SearchableSingleSelectProps) => {
  const hasSingleValue = selectValue !== '';
  const shouldShowClearButton = showAllClear && hasSingleValue && !disabled;

  const selectedLabel =
    options.find((opt) => normalizeOptionValue(opt.value) === selectValue)?.label ??
    fixedBottomOptions?.find((opt) => normalizeOptionValue(opt.value) === selectValue)?.label;
  const selectedLabelText = typeof selectedLabel === 'string' ? selectedLabel : '';

  const listboxId = id ? `${id}-listbox` : undefined;
  const activeOptionId = activeIndex >= 0 && id ? `${id}-option-${activeIndex}` : undefined;

  const handleOptionClick = (option: SelectOption) => {
    onValueChange(normalizeOptionValue(option.value));
    onSearchOpenChange(false);
  };

  const inputValue = searchOpen ? searchValue : selectedLabelText;
  const inputPlaceholder = searchOpen ? '検索...' : placeholder;

  return (
    <div className={cn('relative w-full', rootClassName)}>
      <PopoverRoot open={searchOpen} onOpenChange={onSearchOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          <div className={cn('relative w-full', rootClassName)}>
            <input
              type="text"
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={searchOpen}
              aria-controls={listboxId}
              aria-activedescendant={activeOptionId}
              disabled={disabled}
              id={id}
              aria-invalid={hasError || undefined}
              autoComplete="off"
              value={inputValue}
              placeholder={inputPlaceholder}
              onChange={(e) => {
                onSearchValueChange(e.target.value);
                if (!searchOpen) onSearchOpenChange(true);
              }}
              onKeyDown={onSearchKeyDown}
              className={cn(
                selectTriggerStyles({ height: 'fixed' }),
                'cursor-text',
                formFieldSizeVariants({ size }),
                shouldShowClearButton && 'pr-10',
                !searchOpen && !selectedLabelText && 'text-text-muted',
                triggerClassName,
              )}
            />
            {!shouldShowClearButton && (
              <ChevronDownIcon className="pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 opacity-50" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className={cn(
            'bg-surface-base',
            'flex w-(--radix-popover-trigger-width) flex-col p-0',
            contentClassName,
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
        >
          <SearchableSingleSelectContent
            filteredOptions={filteredOptions}
            activeIndex={activeIndex}
            selectValue={selectValue}
            onOptionClick={handleOptionClick}
            id={id}
            listboxId={listboxId}
            fixedBottomOptions={fixedBottomOptions}
          />
        </PopoverContent>
      </PopoverRoot>
      {shouldShowClearButton && (
        <Button
          variant="ghost"
          size="none"
          onClick={onClear}
          className="absolute top-1/2 right-3 -translate-y-1/2"
          aria-label="選択をクリア"
          tabIndex={-1}
        >
          <XMarkIcon className="text-text-muted" />
        </Button>
      )}
    </div>
  );
};
